// Ações Rápidas - Bloco 11
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Zap, Send, Bell, CheckSquare, History, Users, BookOpen,
  Mail, MessageSquare, Clock, Loader2, Eye, Plus
} from 'lucide-react';

const API_URL = window.location.origin;

export function AcoesRapidas({ year, trimestre, filtros }) {
  const [historico, setHistorico] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedProfessor, setSelectedProfessor] = useState('');
  const [selectedTurma, setSelectedTurma] = useState('');
  const [previewNotificacao, setPreviewNotificacao] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    loadHistorico();
  }, [year, trimestre]);

  const loadHistorico = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/pendencias-academicas/acoes/historico?year=${year}&trimestre=${encodeURIComponent(trimestre)}`);
      const json = await res.json();
      setHistorico(json);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    } finally {
      setLoading(false);
    }
  };

  const notificarProfessor = async () => {
    if (!selectedProfessor) {
      toast.error('Selecione um professor');
      return;
    }
    
    setActionLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/pendencias-academicas/acoes/notificar-professor?professor_id=${selectedProfessor}&year=${year}&trimestre=${encodeURIComponent(trimestre)}`,
        { method: 'POST' }
      );
      const json = await res.json();
      
      if (json.success) {
        setPreviewNotificacao(json.preview);
        setShowPreview(true);
        toast.success('Notificação preparada com sucesso!');
        loadHistorico();
      }
    } catch (error) {
      console.error('Erro ao notificar:', error);
      toast.error('Erro ao criar notificação');
    } finally {
      setActionLoading(false);
    }
  };

  const notificarTurma = async () => {
    if (!selectedTurma) {
      toast.error('Selecione uma turma');
      return;
    }
    
    setActionLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/pendencias-academicas/acoes/notificar-turma?class_group_id=${selectedTurma}&year=${year}&trimestre=${encodeURIComponent(trimestre)}`,
        { method: 'POST' }
      );
      const json = await res.json();
      
      if (json.success) {
        setPreviewNotificacao(json.preview);
        setShowPreview(true);
        toast.success('Relatório preparado com sucesso!');
        loadHistorico();
      }
    } catch (error) {
      console.error('Erro ao notificar:', error);
      toast.error('Erro ao criar relatório');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="acoes-rapidas">
      {/* Header */}
      <Card className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white border-0">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <Zap className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Ações Rápidas</h2>
              <p className="text-blue-100">Gerencie pendências com eficiência</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ações Disponíveis */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Notificar Professor */}
        <Card className="border-blue-100">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Notificar Professor
            </CardTitle>
            <CardDescription>
              Gerar resumo de pendências para um professor
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Select value={selectedProfessor} onValueChange={setSelectedProfessor}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Selecione o professor" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-[300px]">
                  {(filtros?.professores || []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button 
                onClick={notificarProfessor}
                disabled={actionLoading || !selectedProfessor}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Gerar Notificação
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Notificar Turma */}
        <Card className="border-green-100">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-green-600" />
              Relatório de Turma
            </CardTitle>
            <CardDescription>
              Gerar relatório de pendências de uma turma
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Select value={selectedTurma} onValueChange={setSelectedTurma}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Selecione a turma" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-[300px]">
                  {(filtros?.turmas || []).map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button 
                onClick={notificarTurma}
                disabled={actionLoading || !selectedTurma}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Gerar Relatório
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Histórico de Ações */}
      <Card className="border-purple-100">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="w-5 h-5 text-purple-600" />
            Histórico de Ações
          </CardTitle>
          <CardDescription>
            Últimas ações realizadas no sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          {historico?.totais && (
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{historico.totais.notificacoes}</div>
                <div className="text-xs text-blue-600">Notificações</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{historico.totais.lembretes}</div>
                <div className="text-xs text-green-600">Lembretes</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{historico.totais.revisoes}</div>
                <div className="text-xs text-purple-600">Revisões</div>
              </div>
            </div>
          )}
          
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {historico?.timeline?.length > 0 ? (
              historico.timeline.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className={`p-2 rounded-lg ${
                    item.tipo === 'notificacao' ? 'bg-blue-100' :
                    item.tipo === 'lembrete' ? 'bg-green-100' : 'bg-purple-100'
                  }`}>
                    {item.tipo === 'notificacao' ? <Mail className="w-4 h-4 text-blue-600" /> :
                     item.tipo === 'lembrete' ? <Bell className="w-4 h-4 text-green-600" /> :
                     <CheckSquare className="w-4 h-4 text-purple-600" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-700">{item.descricao}</span>
                      <Badge variant="outline" className="text-xs">
                        {item.tipo}
                      </Badge>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {new Date(item.data).toLocaleString('pt-BR')}
                    </div>
                    {item.detalhes?.destinatario_nome && (
                      <div className="text-sm text-slate-600 mt-1">
                        Para: {item.detalhes.destinatario_nome}
                      </div>
                    )}
                  </div>
                  {item.detalhes?.mensagem && (
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>
                            {item.detalhes?.assunto || 'Detalhes'}
                          </DialogTitle>
                        </DialogHeader>
                        <div className="bg-slate-50 p-4 rounded-lg whitespace-pre-wrap text-sm">
                          {item.detalhes.mensagem}
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-slate-500">
                <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Nenhuma ação registrada ainda</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Dialog de Preview */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Preview da Notificação
            </DialogTitle>
          </DialogHeader>
          {previewNotificacao && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Para:</span>
                <Badge>{previewNotificacao.destinatario || previewNotificacao.turma}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Assunto:</span>
                <span className="font-medium">{previewNotificacao.assunto}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Pendências:</span>
                <Badge className="bg-red-100 text-red-700">
                  {previewNotificacao.total_pendentes} pendentes
                  {previewNotificacao.criticas > 0 && ` (${previewNotificacao.criticas} críticas)`}
                </Badge>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <pre className="whitespace-pre-wrap text-sm font-mono">
                  {previewNotificacao.mensagem}
                </pre>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Fechar
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Send className="w-4 h-4 mr-2" />
              Enviar (Em breve)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
